import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Plus, Sparkles, History, Trash2, Search, FileText, Shield, ChevronRight, X } from "lucide-react";
import dayjs from "dayjs";

const CLASSIFICATION_LABEL = {
  canonical: "Canonical",
  foundational_archive: "Foundational Archive",
  operational: "Operational",
  program_source: "Program Source",
  founder_library: "Founder Library",
  research_experimental: "Research / Experimental",
  draft: "Draft",
  superseded: "Superseded",
  restricted: "Restricted",
  public: "Public",
};

const DESTINATION_LABEL = {
  archives: "Archives & Legacy",
  knowledge_vault: "Knowledge Vault",
  charter: "Charter",
  brand_standards: "Brand Standards",
  program_library: "Program Library",
  research_institute: "Research Institute",
  observatory: "Observatory",
  culture: "Culture & Arts",
  healing: "Healing",
  education: "Education",
  community: "Community",
  funding: "Funding",
  administration: "Administration",
  founder_library: "Founder Library",
};

const STATUS_STYLE = {
  imported: "bg-ink-soft/15 text-ink-soft",
  classified: "bg-forest/10 text-forest",
  needs_review: "bg-terracotta/15 text-terracotta",
  approved: "bg-forest text-bone",
  rejected: "bg-terracotta/25 text-terracotta",
  superseded: "bg-ink-soft/25 text-ink-soft",
  published: "bg-terracotta text-bone",
  operationalized: "bg-forest/80 text-bone",
};

const STATUS_LABEL = {
  imported: "Imported",
  classified: "Classified",
  needs_review: "Needs Review",
  approved: "Approved",
  rejected: "Rejected",
  superseded: "Superseded",
  published: "Published",
  operationalized: "Operationalized",
};

export default function Archive() {
  const [vocab, setVocab] = useState(null);
  const [sources, setSources] = useState([]);
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState({ status: "all", classification: "all", destination: "all", q: "" });
  const [newOpen, setNewOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);

  const loadAll = async () => {
    const params = {};
    if (filters.status !== "all") params.status = filters.status;
    if (filters.classification !== "all") params.classification = filters.classification;
    if (filters.destination !== "all") params.destination = filters.destination;
    if (filters.q) params.q = filters.q;
    const [srcRes, statsRes] = await Promise.all([
      api.get("/archive/sources", { params }),
      api.get("/archive/stats"),
    ]);
    setSources(srcRes.data);
    setStats(statsRes.data);
  };

  useEffect(() => {
    api.get("/archive/vocabulary").then(({ data }) => setVocab(data)).catch(() => setVocab({ classifications: [], destinations: [], statuses: [], source_types: [], extraction_types: [] }));
  }, []);
  useEffect(() => { loadAll(); }, [filters.status, filters.classification, filters.destination]);

  const runSearch = (e) => { e?.preventDefault?.(); loadAll(); };

  if (!vocab) return <div className="text-ink-soft text-sm">Loading vocabulary…</div>;

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline">Executive · Archive</div>
          <h1 className="font-serif text-4xl mt-1">Institutional Archive</h1>
          <p className="text-ink-soft mt-2 max-w-3xl leading-relaxed">
            Preserve first. Classify second. Extract third. Approve fourth. Every source enters here — canonical documents,
            legacy writings, transcripts, notes — and moves through review before any portion becomes institutional policy.
          </p>
        </div>
        <button onClick={() => setNewOpen(true)} data-testid="new-source-btn" className="pill pill-primary text-sm">
          <Plus className="h-4 w-4" /> New Source
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Tile label="Total sources" value={stats.total} testId="stat-total" />
          <Tile label="Needs review" value={stats.by_status?.needs_review || 0} testId="stat-review" accent="terracotta" />
          <Tile label="Approved" value={stats.by_status?.approved || 0} testId="stat-approved" />
          <Tile label="Canonical" value={stats.canonical_count || 0} testId="stat-canonical" icon={Shield} />
        </div>
      )}

      {/* Filters */}
      <form onSubmit={runSearch} className="bg-admin-surface border border-line rounded-xl p-4 grid md:grid-cols-4 gap-3 items-end">
        <div>
          <Label className="overline">Status</Label>
          <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
            <SelectTrigger data-testid="filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              {vocab.statuses.map(s => <SelectItem key={s} value={s}>{STATUS_LABEL[s] || s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="overline">Classification</Label>
          <Select value={filters.classification} onValueChange={(v) => setFilters({ ...filters, classification: v })}>
            <SelectTrigger data-testid="filter-classification"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              {vocab.classifications.map(c => <SelectItem key={c} value={c}>{CLASSIFICATION_LABEL[c] || c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="overline">Destination</Label>
          <Select value={filters.destination} onValueChange={(v) => setFilters({ ...filters, destination: v })}>
            <SelectTrigger data-testid="filter-destination"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              {vocab.destinations.map(d => <SelectItem key={d} value={d}>{DESTINATION_LABEL[d] || d}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="overline">Search</Label>
          <div className="flex gap-2">
            <Input value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} placeholder="Title, body, notes, author…" data-testid="filter-q" />
            <button type="submit" className="pill pill-outline text-sm" data-testid="filter-submit"><Search className="h-3.5 w-3.5" /></button>
          </div>
        </div>
      </form>

      {/* Sources list */}
      <div className="space-y-3">
        {sources.length === 0 && <div className="text-ink-soft text-sm py-6">No sources match the current filters.</div>}
        {sources.map(s => (
          <button
            key={s.id}
            onClick={() => setDetailId(s.id)}
            data-testid={`source-row-${s.id}`}
            className="w-full text-left bg-admin-surface border border-line rounded-xl p-5 hover:border-forest transition-colors group"
          >
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap mb-2">
                  <Badge className={STATUS_STYLE[s.review_status] || "bg-ink-soft/15 text-ink-soft"}>
                    {STATUS_LABEL[s.review_status] || s.review_status}
                  </Badge>
                  {(s.classifications || []).slice(0, 3).map(c => (
                    <Badge key={c} className="bg-forest/10 text-forest text-[10px] uppercase tracking-widest">{CLASSIFICATION_LABEL[c] || c}</Badge>
                  ))}
                  {(s.classifications || []).includes("canonical") && (
                    <Shield className="h-3.5 w-3.5 text-terracotta" />
                  )}
                </div>
                <div className="font-serif text-lg text-ink leading-tight">{s.title}</div>
                {(s.body_preview || s.body) && (
                  <div className="text-sm text-ink-soft mt-1 leading-relaxed line-clamp-2">{s.body_preview || s.body}</div>
                )}
                <div className="text-[10px] text-ink-soft mt-2 uppercase tracking-widest">
                  {s.author ? `${s.author} · ` : ""}
                  {s.updated_at ? dayjs(s.updated_at).format("MMM D, YYYY") : ""}
                  {s.destinations?.length ? ` · ${s.destinations.length} destinations` : ""}
                  {s.version ? ` · v${s.version}` : ""}
                </div>
              </div>
              <ChevronRight className="h-5 w-5 text-ink-soft group-hover:text-forest shrink-0 mt-1" />
            </div>
          </button>
        ))}
      </div>

      {newOpen && <NewSourceDialog vocab={vocab} onClose={() => setNewOpen(false)} onCreated={() => { setNewOpen(false); loadAll(); }} />}
      {detailId && <SourceDetail sid={detailId} vocab={vocab} onClose={() => setDetailId(null)} onUpdated={loadAll} />}
    </div>
  );
}

function Tile({ label, value, testId, accent, icon: Icon }) {
  const valueCls = accent === "terracotta" ? "text-terracotta" : "text-ink";
  return (
    <div className="bg-admin-surface border border-line rounded-xl p-4" data-testid={testId}>
      <div className="overline text-ink-soft flex items-center gap-1.5">{Icon && <Icon className="h-3 w-3" />} {label}</div>
      <div className={`font-serif text-3xl mt-1 ${valueCls}`}>{value}</div>
    </div>
  );
}

// ============================================================================
// New Source dialog
// ============================================================================

function NewSourceDialog({ vocab, onClose, onCreated }) {
  const [form, setForm] = useState({
    title: "", body: "", source_type: "text", author: "", original_date: "",
    notes: "", classifications: [], destinations: [], review_status: "imported",
  });
  const [saving, setSaving] = useState(false);

  const toggleArr = (field, val) => {
    setForm(f => {
      const arr = f[field].includes(val) ? f[field].filter(x => x !== val) : [...f[field], val];
      return { ...f, [field]: arr };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error("Title required");
    if (!form.body.trim()) return toast.error("Body required");
    setSaving(true);
    try {
      await api.post("/archive/sources", form);
      toast.success("Source added — preserved with canonical protection");
      onCreated();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.error === "duplicate_source") {
        toast.error(`Duplicate — matches existing source "${detail.existing_title}"`);
      } else {
        toast.error(typeof detail === "string" ? detail : "Save failed");
      }
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-bone max-w-3xl max-h-[90vh] overflow-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">New archive source</DialogTitle>
          <DialogDescription className="text-ink-soft">Preserve the original. Classify next. Nothing becomes canonical automatically.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label className="overline">Title</Label>
            <Input required data-testid="ns-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="overline">Source type</Label>
              <Select value={form.source_type} onValueChange={(v) => setForm({ ...form, source_type: v })}>
                <SelectTrigger data-testid="ns-type"><SelectValue /></SelectTrigger>
                <SelectContent>{vocab.source_types.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="overline">Initial status</Label>
              <Select value={form.review_status} onValueChange={(v) => setForm({ ...form, review_status: v })}>
                <SelectTrigger data-testid="ns-status"><SelectValue /></SelectTrigger>
                <SelectContent>{vocab.statuses.map(s => <SelectItem key={s} value={s}>{STATUS_LABEL[s]}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="overline">Author (optional)</Label>
              <Input data-testid="ns-author" value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} />
            </div>
            <div>
              <Label className="overline">Original date (optional)</Label>
              <Input type="date" data-testid="ns-date" value={form.original_date} onChange={(e) => setForm({ ...form, original_date: e.target.value })} />
            </div>
          </div>
          <div>
            <Label className="overline">Body (paste the source text)</Label>
            <Textarea required rows={10} data-testid="ns-body" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} placeholder="Paste the original document text here. It will be preserved unchanged and checksummed for duplicate detection." />
          </div>
          <div>
            <Label className="overline">Classifications (multi-select)</Label>
            <div className="flex flex-wrap gap-1.5 mt-1.5">
              {vocab.classifications.map(c => (
                <button key={c} type="button" data-testid={`ns-cls-${c}`}
                  onClick={() => toggleArr("classifications", c)}
                  className={`px-2.5 py-1 rounded-md text-[10px] uppercase tracking-widest border transition-colors ${form.classifications.includes(c) ? "bg-forest text-bone border-forest" : "bg-bone text-ink-soft border-line hover:border-forest"}`}
                >{CLASSIFICATION_LABEL[c] || c}</button>
              ))}
            </div>
          </div>
          <div>
            <Label className="overline">Destinations (multi-select)</Label>
            <div className="flex flex-wrap gap-1.5 mt-1.5">
              {vocab.destinations.map(d => (
                <button key={d} type="button" data-testid={`ns-dest-${d}`}
                  onClick={() => toggleArr("destinations", d)}
                  className={`px-2.5 py-1 rounded-md text-[10px] uppercase tracking-widest border transition-colors ${form.destinations.includes(d) ? "bg-terracotta text-bone border-terracotta" : "bg-bone text-ink-soft border-line hover:border-terracotta"}`}
                >{DESTINATION_LABEL[d] || d}</button>
              ))}
            </div>
          </div>
          <div>
            <Label className="overline">Notes (optional)</Label>
            <Textarea rows={2} data-testid="ns-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
          <button type="submit" disabled={saving} data-testid="ns-submit" className="pill pill-primary mt-2">
            {saving ? "Preserving…" : "Preserve source"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// Source detail dialog
// ============================================================================

function SourceDetail({ sid, vocab, onClose, onUpdated }) {
  const [source, setSource] = useState(null);
  const [draft, setDraft] = useState(null);
  const [revisions, setRevisions] = useState([]);
  const [extractions, setExtractions] = useState([]);
  const [suggesting, setSuggesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showRevisions, setShowRevisions] = useState(false);
  const [changeNote, setChangeNote] = useState("");

  const load = async () => {
    const [s, r, x] = await Promise.all([
      api.get(`/archive/sources/${sid}`),
      api.get(`/archive/sources/${sid}/revisions`),
      api.get(`/archive/sources/${sid}/extractions`),
    ]);
    setSource(s.data);
    setDraft({
      classifications: s.data.classifications || [],
      destinations: s.data.destinations || [],
      review_status: s.data.review_status,
      notes: s.data.notes || "",
    });
    setRevisions(r.data);
    setExtractions(x.data);
  };
  useEffect(() => { load(); }, [sid]);

  if (!source || !draft) return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-bone max-w-4xl"><DialogHeader><DialogTitle>Loading…</DialogTitle><DialogDescription>Fetching source detail.</DialogDescription></DialogHeader></DialogContent>
    </Dialog>
  );

  const toggle = (field, val) => {
    const arr = draft[field].includes(val) ? draft[field].filter(x => x !== val) : [...draft[field], val];
    setDraft({ ...draft, [field]: arr });
  };

  const isDirty = () => (
    JSON.stringify(draft.classifications.slice().sort()) !== JSON.stringify((source.classifications || []).slice().sort()) ||
    JSON.stringify(draft.destinations.slice().sort()) !== JSON.stringify((source.destinations || []).slice().sort()) ||
    draft.review_status !== source.review_status ||
    draft.notes !== (source.notes || "")
  );

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/archive/sources/${sid}`, { ...draft, change_note: changeNote });
      toast.success("Saved — a revision entry was journaled");
      setChangeNote("");
      await load();
      onUpdated?.();
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const suggest = async () => {
    setSuggesting(true);
    try {
      await api.post(`/archive/sources/${sid}/suggest`);
      toast.success("AI suggestions ready — nothing applied automatically");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Suggestion failed");
    } finally { setSuggesting(false); }
  };

  const applySuggestion = (field, values) => {
    setDraft({ ...draft, [field]: values });
    toast.info(`Applied suggested ${field} — review then save`);
  };

  const del = async () => {
    if (!confirm(`Delete "${source.title}"? Canonical sources cannot be deleted — change status to Superseded instead.`)) return;
    try {
      await api.delete(`/archive/sources/${sid}`);
      toast.success("Source deleted");
      onClose();
      onUpdated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const ai = source.ai_suggestions || {};

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-bone max-w-4xl max-h-[92vh] overflow-auto">
        <DialogHeader>
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <DialogTitle className="font-serif text-2xl leading-tight">{source.title}</DialogTitle>
              <DialogDescription className="text-ink-soft text-xs mt-1">
                v{source.version} · {source.source_type} · {source.author || "unknown author"} · updated {dayjs(source.updated_at).format("MMM D, YYYY · HH:mm")}
              </DialogDescription>
            </div>
            <button onClick={onClose} className="text-ink-soft hover:text-ink"><X className="h-5 w-5" /></button>
          </div>
        </DialogHeader>

        {/* AI suggest bar */}
        <div className="flex items-center justify-between gap-3 bg-forest/5 border border-forest/20 rounded-lg p-3">
          <div className="text-xs text-ink-soft leading-relaxed">
            <strong className="text-forest">AI-suggested classification</strong> uses Claude Sonnet via Emergent LLM — read-only proposals, nothing is applied automatically. You review and decide.
          </div>
          <button onClick={suggest} disabled={suggesting} data-testid="sd-suggest" className="pill pill-primary text-xs shrink-0">
            <Sparkles className="h-3.5 w-3.5" /> {suggesting ? "Analyzing…" : "Suggest"}
          </button>
        </div>

        {/* AI suggestions preview */}
        {ai.suggested_classifications || ai.suggested_destinations || ai.summary || ai.key_themes ? (
          <section className="bg-terracotta/5 border border-terracotta/25 rounded-lg p-4 text-sm space-y-3" data-testid="sd-ai-panel">
            <div className="overline text-terracotta">AI proposal · {ai.generated_at ? dayjs(ai.generated_at).format("MMM D · HH:mm") : ""}</div>
            {ai.summary && <div><span className="overline text-ink-soft">Summary</span><p className="text-ink leading-relaxed mt-1">{ai.summary}</p></div>}
            {ai.key_themes?.length ? (
              <div>
                <span className="overline text-ink-soft">Key themes</span>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {ai.key_themes.map((t, i) => <Badge key={`theme-${i}-${t}`} className="bg-bone/60 text-ink border border-line">{t}</Badge>)}
                </div>
              </div>
            ) : null}
            {ai.suggested_classifications?.length ? (
              <div className="flex items-center justify-between gap-3">
                <div>
                  <span className="overline text-ink-soft">Suggested classifications</span>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {ai.suggested_classifications.map(c => <Badge key={c} className="bg-forest/10 text-forest">{CLASSIFICATION_LABEL[c] || c}</Badge>)}
                  </div>
                </div>
                <button onClick={() => applySuggestion("classifications", ai.suggested_classifications)} className="text-xs uppercase tracking-widest text-terracotta hover:underline shrink-0" data-testid="sd-apply-cls">Apply</button>
              </div>
            ) : null}
            {ai.suggested_destinations?.length ? (
              <div className="flex items-center justify-between gap-3">
                <div>
                  <span className="overline text-ink-soft">Suggested destinations</span>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {ai.suggested_destinations.map(d => <Badge key={d} className="bg-terracotta/10 text-terracotta">{DESTINATION_LABEL[d] || d}</Badge>)}
                  </div>
                </div>
                <button onClick={() => applySuggestion("destinations", ai.suggested_destinations)} className="text-xs uppercase tracking-widest text-terracotta hover:underline shrink-0" data-testid="sd-apply-dest">Apply</button>
              </div>
            ) : null}
          </section>
        ) : null}

        {/* Body */}
        <section>
          <div className="overline mb-1.5">Preserved body</div>
          <div className="bg-admin-surface border border-line rounded-lg p-4 text-sm text-ink whitespace-pre-wrap max-h-72 overflow-auto leading-relaxed">{source.body}</div>
          <div className="text-[10px] text-ink-soft mt-1.5">Checksum · {source.checksum?.slice(0, 16)}…</div>
        </section>

        {/* Classifications */}
        <section>
          <div className="overline mb-2">Classifications</div>
          <div className="flex flex-wrap gap-1.5">
            {vocab.classifications.map(c => (
              <button key={c} type="button" data-testid={`sd-cls-${c}`}
                onClick={() => toggle("classifications", c)}
                className={`px-2.5 py-1 rounded-md text-[10px] uppercase tracking-widest border transition-colors ${draft.classifications.includes(c) ? "bg-forest text-bone border-forest" : "bg-bone text-ink-soft border-line hover:border-forest"}`}
              >{CLASSIFICATION_LABEL[c] || c}</button>
            ))}
          </div>
        </section>

        {/* Destinations */}
        <section>
          <div className="overline mb-2">Destinations</div>
          <div className="flex flex-wrap gap-1.5">
            {vocab.destinations.map(d => (
              <button key={d} type="button" data-testid={`sd-dest-${d}`}
                onClick={() => toggle("destinations", d)}
                className={`px-2.5 py-1 rounded-md text-[10px] uppercase tracking-widest border transition-colors ${draft.destinations.includes(d) ? "bg-terracotta text-bone border-terracotta" : "bg-bone text-ink-soft border-line hover:border-terracotta"}`}
              >{DESTINATION_LABEL[d] || d}</button>
            ))}
          </div>
        </section>

        {/* Review status */}
        <section className="grid grid-cols-2 gap-3">
          <div>
            <Label className="overline">Review status</Label>
            <Select value={draft.review_status} onValueChange={(v) => setDraft({ ...draft, review_status: v })}>
              <SelectTrigger data-testid="sd-status"><SelectValue /></SelectTrigger>
              <SelectContent>{vocab.statuses.map(s => <SelectItem key={s} value={s}>{STATUS_LABEL[s]}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="overline">Change note (recommended)</Label>
            <Input value={changeNote} onChange={(e) => setChangeNote(e.target.value)} data-testid="sd-note" placeholder="Why this change?" />
          </div>
        </section>

        <div>
          <Label className="overline">Notes</Label>
          <Textarea rows={2} value={draft.notes} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} data-testid="sd-notes" />
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between border-t border-line pt-4 mt-4">
          <div className="flex items-center gap-2">
            <button onClick={() => setShowRevisions(v => !v)} data-testid="sd-toggle-revs" className="text-xs uppercase tracking-widest text-ink-soft hover:text-forest inline-flex items-center gap-1.5">
              <History className="h-3.5 w-3.5" /> {revisions.length} revision{revisions.length === 1 ? "" : "s"}
            </button>
            <button onClick={del} data-testid="sd-delete" className="text-xs uppercase tracking-widest text-ink-soft hover:text-terracotta inline-flex items-center gap-1.5">
              <Trash2 className="h-3.5 w-3.5" /> Delete
            </button>
          </div>
          <button onClick={save} disabled={!isDirty() || saving} data-testid="sd-save"
            className={`inline-flex items-center gap-1.5 text-xs uppercase tracking-widest px-4 py-2 rounded-md transition-colors ${isDirty() ? "bg-forest text-bone hover:bg-forest/90" : "bg-line text-ink-soft cursor-not-allowed"}`}>
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>

        {/* Revisions */}
        {showRevisions && (
          <section className="border-t border-line pt-4 space-y-3" data-testid="sd-revisions">
            {revisions.length === 0 && <div className="text-ink-soft text-sm">No prior revisions.</div>}
            {revisions.map(r => (
              <div key={r.id} className="bg-admin-surface border border-line rounded-md p-3" data-testid={`sd-rev-${r.id}`}>
                <div className="text-[10px] uppercase tracking-widest text-ink-soft">
                  v{r.previous_version} → v{r.previous_version + 1} · {dayjs(r.changed_at).format("MMM D, YYYY · HH:mm")}
                  {r.changed_by ? ` · ${r.changed_by}` : ""}
                </div>
                <div className="mt-1 text-sm text-ink">Changed fields: {r.changed_fields.join(", ") || "—"}</div>
                {r.change_note && <div className="mt-1 text-xs text-ink-soft italic">&ldquo;{r.change_note}&rdquo;</div>}
              </div>
            ))}
          </section>
        )}
      </DialogContent>
    </Dialog>
  );
}
